import { Purchase } from 'referral-types';

import PaperAirplaneIcon from '@heroicons/react/20/solid/PaperAirplaneIcon';
import { Card } from '../../_misc/layouts/Card';
import { usePurchase } from '../../../context/purchase/usePurchase';
import classNames from 'classnames';
import { Button } from '../../_misc/buttons/Button';

import { ButtonType } from '../../_misc/buttons/ButtonType';
import { useDotsLabel } from '../../../hooks/useDotsLabel';
import { UserBadge } from '../../_misc/badge/UserBadge';
import { valToDateString } from '../../../utils';
import { ThemeBackground, ThemeText } from '../../../context/theme/ThemeConstantsUpdated';

const { borderBackground } = ThemeBackground;
const { mainText, secondaryText } = ThemeText;

export const PurchaseInfo = (props: Props) => {
  const { purchase, onContinue } = props;
  const { completedPurchase, isRefreshingToken } = usePurchase();
  const statusSuffix = useDotsLabel();

  const appliedDiscounts = (completedPurchase?.subTotal || 0) - purchase.storeCreditsUsed - purchase.totalPaid;

  return (
    <>
      <Card
        header={
          <div className={classNames('border-b pb-3 text-left', borderBackground)}>
            <h3 className={classNames(secondaryText, '')}>Thank you!</h3>
            <h1 className={classNames(mainText, 'text-4xl font-semibold')}>Order Complete</h1>
          </div>
        }
        footer={
          <div className="flex items-center justify-between pb-5 pt-4">
            {isRefreshingToken ? (
              <span className={classNames(secondaryText, 'font-medium')}>Refreshing Profile{statusSuffix}</span>
            ) : (
              <div />
            )}
            <div className="flex items-center justify-end gap-5">
              <Button onClick={() => onContinue(completedPurchase)} buttonType={ButtonType.Link}>
                Order History
              </Button>
              <Button
                Icon={PaperAirplaneIcon}
                onClick={() => onContinue(completedPurchase)}
                disabled={isRefreshingToken}
                options={{ tooltip: isRefreshingToken ? 'Refreshing Profile' : undefined }}
              >
                Continue
              </Button>
            </div>
          </div>
        }
      >
        <>
          <div className={classNames('space-y-0.5 px-0.5 py-2 text-sm')}>
            <div className="flex justify-between">
              <span className={classNames(mainText, 'font-semibold')}>Order Id</span>
              <span className={classNames(secondaryText)}>{purchase.id}</span>
            </div>

            <div className="flex justify-between">
              <span className={classNames(mainText, 'font-semibold')}>Order Date</span>
              <span className={classNames(secondaryText)}>{valToDateString(purchase.createdDateTime)}</span>
            </div>
          </div>

          <div className={classNames(borderBackground, 'border-t py-2')}>
            {purchase.productSnapshots.map((product) => (
              <div key={product.id} className="flex items-center justify-between px-0.5 py-1">
                <div className="mr-2 flex items-center justify-start gap-3">
                  <h3 className={classNames(mainText, 'text-left text-2xl font-semibold')}>{product.title}</h3>
                  <UserBadge>{product.productType.toUpperCase()}</UserBadge>
                </div>
                <p className={classNames(secondaryText, 'flex-none text-lg font-medium')}>
                  ${product.price.toFixed(2)}
                </p>
              </div>
            ))}
          </div>

          <div className={classNames(borderBackground, 'space-y-0.5 border-t py-2 text-sm')}>
            <div className="flex justify-between px-0.5">
              <span className={classNames(mainText, 'font-semibold')}>Subtotal</span>
              <span className={classNames(secondaryText)}>${purchase.subTotal?.toFixed(2)}</span>
            </div>

            <div className="flex justify-between px-0.5">
              <span className={classNames(mainText, 'font-semibold')}>Discounts</span>
              <span className={classNames(secondaryText)}>${appliedDiscounts.toFixed(2)}</span>
            </div>

            <div className="flex justify-between px-0.5">
              <span className={classNames(mainText, 'font-semibold')}>Credits</span>
              <span className={classNames(secondaryText)}>${purchase.storeCreditsUsed.toFixed(2)}</span>
            </div>
          </div>

          <div
            className={classNames(
              borderBackground,
              'flex items-center justify-between border-t px-0.5 pb-4 pt-3 text-xl font-semibold'
            )}
          >
            <span className={classNames(mainText)}>Total</span>
            <span className={secondaryText}>${purchase.totalPaid.toFixed(2)}</span>
          </div>
        </>
      </Card>
    </>
  );
};

type Props = {
  purchase: Purchase;
  onContinue: (purchase?: Purchase) => void;
};
